import { get, isNil, sumBy } from 'lodash';
import { DateTime } from 'luxon';

import { UnderwritingOutput, ValueConclusion } from 'types/underwriting/underwriting';
import { RentRollUnit } from 'types/underwriting/income-approach';
import { divide, roundToFourDecimals } from 'shared/utils/numberOperations';
import { FinalValues, ValueConclusionInputs } from './types';
import { DEFAULT_FINAL_VALUES, mapValuesToDisplay } from './helpers';
import { Adjustment, LossItem, RentLossItem } from 'types/underwriting/income-approach/cap-rate-conclusion';
import { incomeApproachCalculations } from 'shared/calculations/incomeApproach';
import { LOSS_ITEM_KEYS } from './constants';

export const calculateMonthlyRentLoss = (units: RentRollUnit[], vcLossPercentage: number) => {
  const lossPercentage = 1 - divide(vcLossPercentage, 100);
  return units.reduce((sum: number, unit: RentRollUnit) => sum + (unit.rent || 0), 0) * lossPercentage;
};

export const getFinalValues = (allValues: UnderwritingOutput) => {
  const asCompleteCommercialRentLossItems = get(
    allValues,
    'incomeApproach.capRateConclusion.asCompleteCommercialRentLossItems',
    []
  );
  const asCompleteLossItems = get(allValues, 'incomeApproach.capRateConclusion.asCompleteLossItems', []);
  const asCompleteNpvAdjustments = get(allValues, 'incomeApproach.capRateConclusion.asCompleteNpvAdjustments', []);
  const asCompleteResidentialRentLossItems = get(
    allValues,
    'incomeApproach.capRateConclusion.asCompleteResidentialRentLossItems',
    []
  );
  const asStabilizedCommercialRentLossItems = get(
    allValues,
    'incomeApproach.capRateConclusion.asStabilizedCommercialRentLossItems',
    []
  );
  const asStabilizedLossItems = get(allValues, 'incomeApproach.capRateConclusion.asStabilizedLossItems', []);
  const asStabilizedNpvAdjustments = get(allValues, 'incomeApproach.capRateConclusion.asStabilizedNpvAdjustments', []);
  const asStabilizedResidentialRentLossItems = get(
    allValues,
    'incomeApproach.capRateConclusion.asStabilizedResidentialRentLossItems',
    []
  );
  const npvAdjustments = get(allValues, 'incomeApproach.capRateConclusion.npvAdjustments', []);

  const valueConclusions = get(allValues, 'settings.valueConclusions', []) as ValueConclusion[];
  const valueAsComplete = valueConclusions.includes('asComplete');
  const valueAsStabilized = valueConclusions.includes('asStabilized');

  ////////////////////////////////////////////////////////////////////////

  const includeAsCompleteDiscountRate = get(
    allValues,
    'incomeApproach.capRateConclusion.includeAsCompleteDiscountRate',
    false
  );
  const asCompleteDiscountPercentage = get(
    allValues,
    'incomeApproach.capRateConclusion.asCompleteDiscountPercentage',
    0
  );
  const asCompleteMonthsDiscounted = get(allValues, 'incomeApproach.capRateConclusion.asCompleteMonthsDiscounted', 0);
  const asCompleteDiscountRate = includeAsCompleteDiscountRate
    ? calculateDiscountRate(divide(asCompleteDiscountPercentage, 100), asCompleteMonthsDiscounted)
    : 0;

  const includeAsStabilizedDiscountRate = get(
    allValues,
    'incomeApproach.capRateConclusion.includeAsStabilizedDiscountRate',
    false
  );
  const asStabilizedDiscountPercentage = get(
    allValues,
    'incomeApproach.capRateConclusion.asStabilizedDiscountPercentage',
    0
  );
  const asStabilizedMonthsDiscounted = get(
    allValues,
    'incomeApproach.capRateConclusion.asStabilizedMonthsDiscounted',
    0
  );
  const asStabilizedDiscountRate = includeAsStabilizedDiscountRate
    ? calculateDiscountRate(divide(asStabilizedDiscountPercentage, 100), asStabilizedMonthsDiscounted)
    : 0;

  const roundingFactor = get(allValues, 'incomeApproach.capRateConclusion.roundingFactor') || 0;
  const squareFootage = get(allValues, 'property.grossBuildingArea') || 0;
  const residentialUnits = get(allValues, 'property.residentialUnits', 0);
  const commercialUnits = get(allValues, 'property.commercialUnits', 0);
  const totalUnits = residentialUnits + commercialUnits;

  const netOperatingIncome = incomeApproachCalculations.calculateNetOperatingIncome(allValues) || 0;
  const concludedCapRate = get(allValues, 'incomeApproach.capRateConclusion.capRate') || 0;

  const indicatedValue = calculateIndicatedValue(netOperatingIncome, divide(concludedCapRate, 100));

  const finalValuesOfConclusion = calculateFinalValues({
    indicatedValue,
    npvAdjustments,
    asStabilizedNpvAdjustments,
    asCompleteNpvAdjustments,
    asStabilizedResidentialRentLossItems,
    asCompleteResidentialRentLossItems,
    asStabilizedCommercialRentLossItems,
    asCompleteCommercialRentLossItems,

    asStabilizedLossItems,
    asCompleteLossItems,
    roundingFactor,
    squareFootage,
    unitCount: totalUnits,
    includeValueAsComplete: valueAsComplete,
    includeValueAsStabilized: valueAsStabilized,
    asCompleteDiscountRate,
    asStabilizedDiscountRate,
  });

  return {
    ...mapValuesToDisplay(finalValuesOfConclusion),
    asCompleteDiscountRate,
    asStabilizedDiscountRate,
  };
};

export const calculateFinalValues = ({
  indicatedValue,
  npvAdjustments,
  asStabilizedNpvAdjustments,
  asCompleteNpvAdjustments,
  asStabilizedResidentialRentLossItems,
  asCompleteResidentialRentLossItems,
  asStabilizedCommercialRentLossItems,
  asCompleteCommercialRentLossItems,
  asStabilizedLossItems,
  asCompleteLossItems,
  roundingFactor,
  squareFootage,
  unitCount,
  includeValueAsComplete,
  includeValueAsStabilized,
  asCompleteDiscountRate,
  asStabilizedDiscountRate,
}: ValueConclusionInputs): FinalValues => {
  if (!indicatedValue) {
    return DEFAULT_FINAL_VALUES;
  }
  const totalNpvAdjustment = calculateTotalNpvAdjustments(npvAdjustments || []);
  const finalValueAsStabilized = indicatedValue + totalNpvAdjustment;
  const asStabilizedTotalLossItems: (LossItem | RentLossItem)[] = [
    ...asStabilizedLossItems,
    ...asStabilizedResidentialRentLossItems,
    ...asStabilizedCommercialRentLossItems,
  ];
  const asStabilizedMarketValueWithDiscount = asStabilizedDiscountRate
    ? finalValueAsStabilized * asStabilizedDiscountRate
    : 0;
  const finalValueAsComplete = includeValueAsStabilized
    ? (asStabilizedMarketValueWithDiscount || finalValueAsStabilized) -
      calculateCostOfLossItemsAndAdjustments(asStabilizedTotalLossItems, asStabilizedNpvAdjustments || [])
    : finalValueAsStabilized;

  const asCompleteTotalLossItems: (LossItem | RentLossItem)[] = [
    ...asCompleteLossItems,
    ...asCompleteResidentialRentLossItems,
    ...asCompleteCommercialRentLossItems,
  ];
  const asCompleteMarketValueWithDiscount = asCompleteDiscountRate ? finalValueAsComplete * asCompleteDiscountRate : 0;
  const finalValueAsIs = includeValueAsComplete
    ? (asCompleteMarketValueWithDiscount || finalValueAsComplete) -
      calculateCostOfLossItemsAndAdjustments(asCompleteTotalLossItems, asCompleteNpvAdjustments || [])
    : finalValueAsComplete;

  return {
    indicatedValue: calculateTotalPsfPerUnitAndRoundedValues(indicatedValue, squareFootage, unitCount, roundingFactor),
    finalValueAsComplete: calculateTotalPsfPerUnitAndRoundedValues(
      finalValueAsComplete,
      squareFootage,
      unitCount,
      roundingFactor
    ),
    finalValueAsStabilized: calculateTotalPsfPerUnitAndRoundedValues(
      finalValueAsStabilized,
      squareFootage,
      unitCount,
      roundingFactor
    ),
    finalValueAsIs: calculateTotalPsfPerUnitAndRoundedValues(finalValueAsIs, squareFootage, unitCount, roundingFactor),
    asCompleteMarketValueWithDiscount,
    asStabilizedMarketValueWithDiscount,
  };
};

export const getDatesOfFinalValues = (underwritingData: UnderwritingOutput) => {
  const dateOfValuation = get(underwritingData, 'incomeApproach.capRateConclusion.dateOfValuation');
  const asCompleteMonthsOfRentLoss = get(
    underwritingData,
    'incomeApproach.capRateConclusion.asCompleteMonthsOfRentLoss',
    0
  );
  const asStabilizedMonthsOfRentLoss = get(
    underwritingData,
    'incomeApproach.capRateConclusion.asStabilizedMonthsOfRentLoss',
    0
  );

  if (!dateOfValuation || !DateTime.fromISO(dateOfValuation).isValid) {
    return { dateOfFinalValueAsIs: '', dateOfFinalValueAsComplete: '', dateOfFinalValueAsStabilized: '' };
  }

  const dateOfFinalValueAsIs = DateTime.fromISO(dateOfValuation);
  const dateOfFinalValueAsComplete = dateOfFinalValueAsIs.plus({ months: asCompleteMonthsOfRentLoss });
  const dateOfFinalValueAsStabilized = dateOfFinalValueAsComplete.plus({ months: asStabilizedMonthsOfRentLoss });

  return {
    dateOfFinalValueAsIs: dateOfFinalValueAsIs.toFormat('M/d/yyyy'),
    dateOfFinalValueAsComplete: dateOfFinalValueAsComplete.toFormat('M/d/yyyy'),
    dateOfFinalValueAsStabilized: dateOfFinalValueAsStabilized.toFormat('M/d/yyyy'),
  };
};

export const calculateTotalNpvAdjustments = (npvAdjustments: Adjustment[]) => {
  if (!npvAdjustments?.length) {
    return 0;
  }
  return npvAdjustments.reduce((sum, npvAdjustment) => sum + (npvAdjustment.amount || 0), 0);
};

export const calculateTotalPsfPerUnitAndRoundedValues = (
  finalValue: number,
  squareFootage: number | undefined,
  numberOfUnits: number | undefined,
  roundingFactor: number
) => {
  if (!finalValue) {
    return {
      total: 0,
      rounded: 0,
      perUnit: 0,
      psf: 0,
    };
  }

  const finalValueRounded = (Math.round(finalValue / roundingFactor) || 1) * roundingFactor;

  return {
    total: finalValue,
    rounded: finalValueRounded,
    perUnit: divide(finalValueRounded, numberOfUnits),
    psf: divide(finalValueRounded, squareFootage),
  };
};

export const calculateCostOfLossItemsAndAdjustments = (
  lossItems: (LossItem | RentLossItem)[] = [],
  npvAdjustments: Adjustment[] = []
) => {
  let entrepreneurialProfit = 0; // ??
  const totalLossItems = lossItems
    ? lossItems.reduce((sum, lossItem) => {
        const { amount, name } = lossItem || {};
        if (name === LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT) {
          entrepreneurialProfit = lossItem.amount || 0;
          return sum;
        }
        return sum + (amount || 0);
      }, 0)
    : 0;

  let positiveAdjustments = 0;
  const totalNpvAdjustments = npvAdjustments.reduce((sum, npvAdjustment) => {
    const { amount } = npvAdjustment;
    if (!amount) {
      return sum;
    }
    if (amount >= 0) {
      positiveAdjustments += amount;
      return sum;
    }
    return sum + Math.abs(amount);
  }, 0);
  const totalDeductions = totalLossItems + totalNpvAdjustments;
  const entrepreneurialProfitAmount = totalDeductions * (entrepreneurialProfit / 100);

  return totalDeductions + entrepreneurialProfitAmount - positiveAdjustments;
};

export const calculateDiscountRate = (percentage: number, months: number): number => {
  const discountRate = divide(1, (1 + percentage / 12) ** months);
  return roundToFourDecimals(discountRate);
};

export const calculateIndicatedValue = (netOperatingIncome: number, capRate: number): number => {
  return divide(netOperatingIncome, capRate);
};

export const calculateEntrepreneurialProfitTotal = (
  lossItems: LossItem[],
  rentLossItems: RentLossItem[],
  npvAdjustments: Adjustment[]
) => {
  const lossTotal = sumBy(lossItems, (item) =>
    isNil(item) || isNil(item.amount) || item.name === LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT ? 0 : item.amount
  );
  const rentLossTotal = sumBy(rentLossItems, (item) => (isNil(item) || isNil(item.amount) ? 0 : item.amount));
  const adjustmentsTotal = sumBy(npvAdjustments, (adjustment) =>
    isNil(adjustment) || isNil(adjustment.amount) ? 0 : Math.abs(adjustment.amount)
  );
  return lossTotal + rentLossTotal + adjustmentsTotal;
};
